/* eslint-disable eqeqeq */
import { handleTabToNext, looseEq } from '../../formKit';
import { airStreamOptions } from '../../airStreamOptions';
import { useAirStreamOfferedIds } from '../../useAirStreamOfferedIds';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import {
  ABareInput,
  AccessorySelectHeading,
  AccSubtab,
  accList,
  AutoSizeRow,
} from '../accessoryFieldKit';
import AccessoryMountingPopup from '../AccessoryMountingPopup';
import { useSectionInternalWalls } from '../internalWalls';
import {
  ComplexMountingPlaneRow,
  ComplexPlaneRows,
  resolveComplexPlane,
} from '../complexMounting';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — OPENING form (accessories/opening/{view/form.js 202,
 * tmpl/form.tmpl 468}, read in full). §51.2 row:
 *  - no catalogue field; width/height model fields (duct dims) with CMS
 *    min/max clamps; label flips Height→Length on floor/roof planes;
 *  - inline autoSize 2:1 writing width/height, CMS min/max clamped,
 *    defaultFaceVelocity 1200 (parity 331);
 *  - `.sublist` non-clickable greying during every non-{tag,air,airflow}
 *    PATCH (§49.6:1232 — chassis sublistsGreyed; the tmpl sublists are
 *    ALSO statically non-clickable: edits flow through the selection dialog);
 *  - with_position PATCH flows (§20 #55) ride the position store + popup;
 *  - SelectionMode handler (callOnDispatcher) → Select heading.
 */
const config = ACCESSORY_FORM_CONFIGS.opening;

/** opening/view/form.js autoSize — 2:1 ratio + CMS min/max clamps, verbatim. */
const openingAutoSize = (
  auto: boolean,
  model: { get<T = unknown>(k: string): T },
  data: Record<string, unknown>,
  faceVelocityInit?: number | string,
  cmsAccessory?: Record<string, unknown>
): boolean | undefined => {
  if (!data.airflow_actual) return undefined;
  const existingWidth = model.get('width');
  const existingHeight = model.get('height');
  if (!auto && (existingWidth || existingHeight)) return undefined;
  const airflow = Number(data.airflow_actual);
  const faceVelocity =
    Number(faceVelocityInit) || Number(model.get('face_velocity_actual')) || 1200;
  let calculatedHeight = Math.floor(Math.sqrt((144 * airflow) / (faceVelocity * 2)));
  let calculatedWidth = 2 * calculatedHeight;
  const minWidth = Number(cmsAccessory?.min_width);
  const maxWidth = Number(cmsAccessory?.max_width);
  const minHeight = Number(cmsAccessory?.min_height);
  const maxHeight = Number(cmsAccessory?.max_height);
  if (minWidth) calculatedWidth = Math.max(minWidth, calculatedWidth);
  if (maxWidth) calculatedWidth = Math.min(maxWidth, calculatedWidth);
  if (minHeight) calculatedHeight = Math.max(minHeight, calculatedHeight);
  if (maxHeight) calculatedHeight = Math.min(maxHeight, calculatedHeight);
  if (calculatedWidth == existingWidth && calculatedHeight == existingHeight) return undefined;
  data.width = calculatedWidth;
  data.height = calculatedHeight;
  return true;
};

const OpeningForm = ({ row, section, tunnel, cms, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'performance'],
    autoSize: openingAutoSize,
    autoSizeGetState: (obj) => ({ width: obj?.width, height: obj?.height }),
    getFaceVelocityArea: (data) => {
      const m = data.model as Record<string, unknown> | undefined;
      const w = Number(m?.width);
      const h = Number(m?.height);
      return w && h ? w * h : null;
    },
    defaultFaceVelocity: 1200,
  });
  // ★ Above the preloader early-return below: a hook that runs only
  // after the form has loaded changes the hook count between renders
  // (React #310, "Rendered more hooks than during the previous render").
  const offeredAirStreams = useAirStreamOfferedIds();
  const { internalWalls, mountingPlanesOnCabinet } = useSectionInternalWalls(
    Number(api.model?.get('section_id') ?? section.id),
    api.cmsAccessory
  );
  api.setInternalWalls(internalWalls);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const cmsDoc = cms as Record<string, unknown> | undefined;
  const disableMounting = model.get('width') !== null && model.get('height') !== null;
  // The opening's OWN list where the server sends one
  // (`AHUAccessoryOpening.mounting_planes`): the CMS list is per-manufacturer,
  // so it offers a Floor in a section with no floor deck, under-insulation or
  // under-liner to cut one in (Artashes, 2026-10-20). Same shape, same keys —
  // the damper's row has read its own list all along.
  const ownPlanes = (model.get('mounting_planes') as Array<Record<string, unknown>> | null) ?? null;
  const cabinetPlanes = (ownPlanes?.length
    ? ownPlanes.filter((p) => !/^internal_wall_[a-z]+/.test(String(p.value ?? '')))
    : mountingPlanesOnCabinet) as Array<Record<string, unknown> & { id: number }>;
  const resolved = resolveComplexPlane(api, internalWalls, cabinetPlanes);
  const greyExtra = api.sublistsGreyed ? ' non-clickable' : '';
  const shapeOptions = accList(api.cmsAccessory, 'shape');
  const airStreams = airStreamOptions(
    accList(cmsDoc, 'air.air_stream'),
    offeredAirStreams,
    model.get('air_stream_id')
  );
  const heightLabel =
    resolved.planeValue === 'floor' || resolved.planeValue === 'roof' ? 'Length' : 'Height';

  return (
    <>
      <AccessorySelectHeading api={api} type="opening" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab
            api={api}
            type="opening"
            cid={cid}
            name="properties"
            sublistClass={`max-height-auto non-clickable${greyExtra}`}
          >
            <li>
              <div>
                <ABareInput api={api} name="tag" formatter="none" extraClass="text-left" />
                <i className="text-clear icon-close-1" />
                <span className="label">Tag</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {shapeOptions.find((o) => looseEq(o.id, model.get('shape_id'))) ? (
                    <span>
                      {String(
                        shapeOptions.find((o) => looseEq(o.id, model.get('shape_id')))?.name ?? ''
                      )}
                    </span>
                  ) : null}
                </span>
                <select
                  className="model_field"
                  name="shape_id"
                  required
                  value={model.get('shape_id') == null ? 'None' : String(model.get('shape_id'))}
                  onChange={(e) => api.commitModelField('shape_id', e.target.value)}
                >
                  {model.get('shape_id') == null && (
                    <option value="None" className="hide">
                      - -
                    </option>
                  )}
                  {shapeOptions.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label">Shape</span>
              </div>
            </li>
            <li>
              <div className="multiply">
                <span className="unit">in</span>
                <ABareInput
                  api={api}
                  name="width"
                  extraClass="medium-input"
                  min={Number(api.cmsAccessory?.min_width) || undefined}
                  max={Number(api.cmsAccessory?.max_width) || undefined}
                />
                <span className="multiply-sign">x</span>
                <ABareInput
                  api={api}
                  name="height"
                  extraClass="medium-input"
                  min={Number(api.cmsAccessory?.min_height) || undefined}
                  max={Number(api.cmsAccessory?.max_height) || undefined}
                />
                {/* opening form.tmpl:48-60 — both spans are `class=""`, no
                    `.required`, so legacy draws no asterisk here. */}
                <span className="label">
                  <span>Width</span> x <span>{heightLabel}</span>
                </span>
              </div>
            </li>
          </AccSubtab>

          <AccSubtab
            api={api}
            type="opening"
            cid={cid}
            name="mounting"
            label="Mounting"
            liClass="mounting-section-wrapper"
            sublistClass={`non-clickable${disableMounting ? '' : ' non-clickable'}${greyExtra}`}
          >
            <ComplexMountingPlaneRow
              api={api}
              cabinetPlanes={cabinetPlanes}
              internalWalls={internalWalls}
              selectionText={resolved.planeName}
              selectedValue={
                api.position && Number(api.position.get('mounting_plane_id'))
                  ? String(api.position.get('mounting_plane_id'))
                  : 'None'
              }
            />
            <ComplexPlaneRows
              api={api}
              variant="opening"
              planeValue={resolved.planeValue}
              iwPlane={resolved.iwPlane}
            />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="opening"
            cid={cid}
            name="performance"
            label="Performance"
            sublistClass={`performance-sublist non-clickable${greyExtra}`}
          >
            <li>
              <div>
                <span className="unit">CFM</span>
                <span className="selection">
                  {airStreams.find((o) => looseEq(o.id, model.get('air_stream_id'))) ? (
                    <span>
                      {String(
                        airStreams.find((o) => looseEq(o.id, model.get('air_stream_id')))?.name ??
                          ''
                      )}
                    </span>
                  ) : null}
                </span>
                <select
                  name="air_stream_id"
                  className="model_field coli-medium"
                  required
                  data-placeholder=""
                  value={
                    model.get('air_stream_id') == null ? 'None' : String(model.get('air_stream_id'))
                  }
                  onChange={(e) => api.commitModelField('air_stream_id', e.target.value)}
                >
                  <option value="None">- -</option>
                  {airStreams.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}&nbsp;
                    </option>
                  ))}
                </select>
                <ABareInput
                  api={api}
                  name="airflow_actual"
                  formatter="integer"
                  extraClass="coli-medium"
                  min={Number(model.get('min_airflow')) || 0}
                  max={Number(model.get('max_airflow')) || 400000}
                />
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <AutoSizeRow
              api={api}
              tooltip="Opening will be automatically sized based on 2:1 width x height ratio, airflow and desired velocity"
            />
            <li>
              <div>
                <span className="unit">fpm</span>
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="face_velocity_actual"
                  className="model_field coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('face_velocity_actual') ?? '')}
                  onChange={() => undefined}
                />
                <span className="unit long-unit">in WC</span>
                <input
                  className="coli-medium"
                  name="apd"
                  type="text"
                  autoComplete="off"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('apd') ?? '')}
                  onChange={() => undefined}
                />
                <span className="label">Face Velocity | APD</span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
      {/* opening tmpl's popup host div is COMMENTED OUT in legacy — popup
          still opens via the base #popup-only host (parity 333). */}
      <AccessoryMountingPopup api={api} typeName="opening" typeValue="opening" />
    </>
  );
};

export default OpeningForm;
